import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  formatNumber,
  type BaseUnit,
  type IngredientPackage,
  type UnitsBySystem,
} from '@crave/costing';
import { Button, Card, Field, FormGrid, SelectField } from '@crave/ui';
import { api } from '../api.ts';
import { ConfirmDelete } from '../components/ConfirmDelete.tsx';
import { HistoryLink } from '../components/HistoryLink.tsx';
import { UnitSelect, defaultUnitValue, splitUnitValue } from '../components/UnitSelect.tsx';
import { Notice, Section, errorText, money, when } from '../components/ui.tsx';
import { useStaff } from '../lib/staff.tsx';
import { Select } from './Inventory.tsx';

interface Ingredient {
  id: string;
  code: string;
  name: string;
  base_unit: BaseUnit;
  is_prep: boolean;
  is_active?: boolean;
  density_g_per_ml: number | null;
  each_weight_g: number | null;
  display_unit: string | null;
  packages: IngredientPackage[] | null;
  convertible_units?: UnitsBySystem | null;
}
interface Vendor {
  id: string;
  name: string;
  kind: string;
  is_active: boolean;
  uses: { receipts: number; lots: number; planned_purchases: number };
  can_delete: boolean;
  delete_blocked_reason: string | null;
}
interface Receipt {
  id: string;
  purchased_at: string;
  /** draft, posted or void */
  status: string;
  total_cents: number | null;
  reference: string | null;
  vendor: string | null;
  entered_by: string | null;
  line_count: number;
  can_delete?: boolean;
  can_void?: boolean;
  delete_blocked_reason?: string | null;
}
interface Line {
  ingredient_id: string;
  package_count: string;
  package_size: string;
  /** 'unit:<code>' or 'package:<id>' */
  unit: string;
  unit_price: string;
}

const blank = (): Line => ({
  ingredient_id: '',
  package_count: '1',
  package_size: '',
  unit: '',
  unit_price: '',
});

const STATUS_LABEL: Record<string, string> = { draft: 'Draft', posted: 'Posted', void: 'Voided' };

const plural = (n: number, one: string, many = `${one}s`) =>
  `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

/** "1 receipt and 8 lots", "Not used yet". */
function usesText(u: Vendor['uses']): string {
  const parts = [
    u.receipts ? plural(u.receipts, 'receipt') : '',
    u.lots ? plural(u.lots, 'lot') : '',
    u.planned_purchases ? plural(u.planned_purchases, 'planned purchase') : '',
  ].filter(Boolean);
  if (parts.length === 0) return 'Not used yet';
  return parts.length === 1 ? parts[0]! : `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}`;
}

const withReason = (path: string, reason: string | undefined) =>
  reason ? `${path}?reason=${encodeURIComponent(reason)}` : path;

/**
 * Receipt line editor: a header row over aligned columns when the card is wide, and each line as
 * its own box of labelled fields when it is narrow (phones). The per-field labels stay in the DOM
 * for screen readers and are only visually hidden while the header row is showing.
 */
const LINES_CSS = `
.po-lines { container-type: inline-size; display: grid; gap: 8px; margin-top: 4px; }
.po-line {
  display: grid;
  grid-template-columns: minmax(0, 2.2fr) minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 1.7fr) minmax(0, 1.1fr) 44px;
  grid-template-areas: 'ing pk sz unit price rm';
  column-gap: 8px;
  align-items: center;
}
.po-line.po-head { font-size: 14px; font-weight: 600; line-height: 1.3; color: var(--ink-soft); }
.po-line > .crave-field { position: relative; grid-template-rows: auto; row-gap: 0; }
.po-line > .crave-field > .crave-field-label {
  position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap;
}
.po-line > .crave-field > .crave-field-hint { display: none; }
.po-ing { grid-area: ing; }
.po-pk { grid-area: pk; }
.po-sz { grid-area: sz; }
.po-unit { grid-area: unit; }
.po-price { grid-area: price; }
.po-rm { grid-area: rm; }
@container (max-width: 639px) {
  .po-line.po-head { display: none; }
  .po-line {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 44px;
    grid-template-areas: 'ing ing rm' 'pk sz sz' 'unit unit unit' 'price price price';
    row-gap: 12px;
    align-items: end;
    padding: 12px;
    border: 1px solid var(--line);
    border-radius: var(--radius-sm);
  }
  .po-line > .crave-field { grid-template-rows: auto auto; row-gap: 6px; }
  .po-line > .crave-field > .crave-field-label {
    position: static; width: auto; height: auto; overflow: visible; clip-path: none; white-space: normal;
  }
}
/* Receipts and vendors: a table when there is room, one labelled block per row on phones. */
.po-list { container-type: inline-size; min-width: 0; margin-top: 4px; }
.po-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.po-table th { text-align: left; padding: 8px 10px; border-bottom: 2px solid var(--line); color: var(--ink-soft); font-weight: 600; white-space: nowrap; }
.po-table td { padding: 8px 10px; border-bottom: 1px solid var(--line); vertical-align: middle; }
.po-table td.po-empty { padding: 16px; color: var(--ink-soft); }
.po-sub { display: block; font-size: 12px; line-height: 1.35; color: var(--ink-soft); }
.po-label { display: none; }
.po-hidden { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
.po-actions { display: flex; flex-wrap: nowrap; justify-content: flex-end; align-items: center; gap: 8px 12px; }
.po-actions button { min-height: 40px; padding: 0 14px; white-space: nowrap; }
.po-nowrap { white-space: nowrap; }
@container (max-width: 560px) {
  .po-table thead { display: none; }
  .po-table, .po-table tbody { display: block; }
  .po-table tr { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px 12px; padding: 14px 0; border-bottom: 1px solid var(--line); }
  .po-table td { display: block; padding: 0; border: 0; min-width: 0; overflow-wrap: anywhere; }
  .po-table td.po-wide, .po-table td.po-empty { grid-column: 1 / -1; }
  .po-label { display: block; font-size: 12px; line-height: 1.3; color: var(--ink-soft); }
  .po-actions { justify-content: flex-start; flex-wrap: wrap; }
}
.po-inactive { display: inline-block; margin-left: 8px; padding: 1px 8px; border-radius: 999px; font-size: 12px;
  font-weight: 700; color: var(--ink-soft); border: 1px solid var(--line); vertical-align: middle; }
`;

export function Purchasing() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { can, location } = useStaff();
  const canDelete = can('records.delete');
  const canAdjust = can('inventory.manage');
  const newVendorId = useId();
  const ings = useQuery({
    queryKey: ['ingredients'],
    queryFn: async () =>
      (await api.get<{ items: Ingredient[] }>('/admin/recipes/ingredients')).data,
  });
  const vendors = useQuery({
    queryKey: ['vendors'],
    queryFn: async () =>
      (await api.get<{ items: Vendor[] }>('/admin/inventory/vendors')).data.items,
  });
  const receipts = useQuery({
    queryKey: ['receipts', location.slug],
    queryFn: async () =>
      (await api.get<{ items: Receipt[] }>(`/admin/inventory/receipts?location=${location.slug}`))
        .data.items,
  });
  const locations = useQuery({
    queryKey: ['admin-locations'],
    queryFn: async () =>
      (await api.get<{ items: { id: string }[] }>('/admin/locations')).data.items,
  });
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [listMsg, setListMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [vendorMsg, setVendorMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [vendor, setVendor] = useState('');
  const [reference, setReference] = useState('');
  const [lines, setLines] = useState<Line[]>([blank()]);
  const [newVendor, setNewVendor] = useState('');
  const [voiding, setVoiding] = useState<Receipt | null>(null);
  const [deletingReceipt, setDeletingReceipt] = useState<Receipt | null>(null);
  const [deletingVendor, setDeletingVendor] = useState<Vendor | null>(null);

  const refreshReceipts = () => {
    void qc.invalidateQueries({ queryKey: ['receipts'] });
    void qc.invalidateQueries({ queryKey: ['stock'] });
    void qc.invalidateQueries({ queryKey: ['vendors'] });
  };
  const post = useMutation({
    mutationFn: (v: Record<string, unknown>) => api.post('/admin/inventory/receipts', v),
    onSuccess: () => {
      setMsg({ kind: 'ok', text: 'Receipt posted. Stock and food cost are updated.' });
      setLines([blank()]);
      setReference('');
      refreshReceipts();
    },
    onError: (e) => setMsg({ kind: 'error', text: errorText(e, 'Could not post the receipt.') }),
  });
  const addVendor = useMutation({
    mutationFn: async (name: string) =>
      (await api.post<{ id: string }>('/admin/inventory/vendors', { name })).data,
    onSuccess: (v) => {
      setNewVendor('');
      setVendor(v.id);
      void qc.invalidateQueries({ queryKey: ['vendors'] });
    },
    onError: (e) => setMsg({ kind: 'error', text: errorText(e, 'Could not add the vendor.') }),
  });
  const setActive = useMutation({
    mutationFn: async ({ v, active }: { v: Vendor; active: boolean }) => {
      await api.patch(`/admin/inventory/vendors/${v.id}`, { is_active: active });
      return { v, active };
    },
    onSuccess: ({ v, active }) => {
      setVendorMsg({
        kind: 'ok',
        text: active
          ? `${v.name} is active again and shows up when you enter a receipt.`
          : `${v.name} is deactivated. Old receipts keep it, and it no longer shows up for new ones.`,
      });
      if (!active && vendor === v.id) setVendor('');
      void qc.invalidateQueries({ queryKey: ['vendors'] });
    },
    onError: (e) => setVendorMsg({ kind: 'error', text: errorText(e, 'Could not save.') }),
  });

  const locationId = location.id || locations.data?.[0]?.id;
  const ingredientFor = (id: string) => ings.data?.items.find((i) => i.id === id);
  const packageFor = (l: Line) => {
    const f = splitUnitValue(l.unit);
    return 'package_id' in f && f.package_id
      ? ingredientFor(l.ingredient_id)?.packages?.find((p) => p.id === f.package_id)
      : undefined;
  };
  const setLine = (i: number, patch: Partial<Line>) =>
    setLines(lines.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const total = lines.reduce(
    (s, l) => s + Math.round(Number(l.package_count || 0) * Number(l.unit_price || 0) * 100),
    0,
  );
  const valid = lines.every((l) => {
    const f = splitUnitValue(l.unit);
    const sized = 'package_id' in f ? Boolean(f.package_id) : Number(l.package_size) > 0;
    return (
      l.ingredient_id &&
      Number(l.package_count) > 0 &&
      l.unit &&
      sized &&
      l.unit_price.trim() !== '' &&
      Number(l.unit_price) >= 0
    );
  });
  const vendorOptions = (vendors.data ?? [])
    .filter((v) => v.is_active || v.id === vendor)
    .map((v): [string, string] => [v.id, v.is_active ? v.name : `${v.name} (inactive)`]);

  const receiptName = (r: Receipt) =>
    `the receipt${r.reference ? ` “${r.reference}”` : ''}${r.vendor ? ` from ${r.vendor}` : ''} on ${when(r.purchased_at)}`;

  return (
    <Section title="Purchasing">
      <style>{LINES_CSS}</style>
      {locations.isError && (
        <Notice kind="error">
          Could not load your location, so receipts can't be posted.{' '}
          {errorText(locations.error, '')}
        </Notice>
      )}
      <Card style={{ minWidth: 0 }}>
        <strong>Enter a receipt</strong>
        <p style={{ margin: '4px 0 12px', fontSize: 14, color: 'var(--ink-soft)' }}>
          One line per product. For 2 bags of 3 lb flour at $6.73 each, enter Packages 2, Size 3,
          Unit Pound and $6.73 per package. Packages you set up on the Ingredients page are in the
          Unit list too, and they fill in the size. Posting adds the stock and updates food cost
          right away.
        </p>
        <FormGrid min={220}>
          <Select label="Vendor" value={vendor} onChange={setVendor} options={vendorOptions} />
          <Field
            label="Reference (optional)"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Receipt number or note"
          />
          {/* A field whose control row holds the input and its Add button, so they never separate. */}
          <div className="crave-field">
            <label htmlFor={newVendorId} className="crave-field-label">
              New vendor
            </label>
            <div style={{ display: 'flex', gap: 8, minWidth: 0 }}>
              <input
                id={newVendorId}
                value={newVendor}
                onChange={(e) => setNewVendor(e.target.value)}
                placeholder="Name"
                style={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 'var(--touch)',
                  padding: '0 12px',
                  borderRadius: 'var(--radius-sm)',
                  border: '2px solid var(--line)',
                  background: 'var(--paper)',
                  color: 'var(--ink)',
                }}
              />
              <Button
                type="button"
                variant="secondary"
                style={{ flexShrink: 0 }}
                disabled={addVendor.isPending}
                onClick={() => newVendor.trim() && addVendor.mutate(newVendor.trim())}
              >
                Add
              </Button>
            </div>
            <span className="crave-field-hint" />
          </div>
        </FormGrid>
        <div className="po-lines">
          <div className="po-line po-head" aria-hidden="true">
            <span className="po-ing">Ingredient</span>
            <span className="po-pk">Packages</span>
            <span className="po-sz">Size</span>
            <span className="po-unit">Unit</span>
            <span className="po-price">$ per package</span>
          </div>
          {lines.map((l, i) => {
            const ing = ingredientFor(l.ingredient_id);
            const pkg = packageFor(l);
            return (
              <div key={i} className="po-line">
                <SelectField
                  className="po-ing"
                  label="Ingredient"
                  value={l.ingredient_id}
                  onChange={(e) => {
                    const next = ingredientFor(e.target.value);
                    setLine(i, {
                      ingredient_id: e.target.value,
                      unit: defaultUnitValue(next),
                      package_size: '',
                    });
                  }}
                  options={(ings.data?.items ?? [])
                    .filter((x) => !x.is_prep && x.is_active !== false)
                    .map((x) => [x.id, x.name] as const)}
                  placeholder="Choose…"
                />
                <Field
                  className="po-pk"
                  label="Packages"
                  inputMode="decimal"
                  value={l.package_count}
                  onChange={(e) => setLine(i, { package_count: e.target.value })}
                />
                <Field
                  className="po-sz"
                  label="Size"
                  inputMode="decimal"
                  value={pkg ? formatNumber(Number(pkg.quantity), 3) : l.package_size}
                  readOnly={Boolean(pkg)}
                  aria-readonly={pkg ? true : undefined}
                  title={pkg ? 'Set by the package you chose' : undefined}
                  style={pkg ? { background: 'var(--mist)' } : undefined}
                  onChange={(e) => setLine(i, { package_size: e.target.value })}
                />
                <UnitSelect
                  className="po-unit"
                  ingredient={ing}
                  value={l.unit}
                  placeholder={ing ? undefined : 'Choose…'}
                  showUnavailableHint={false}
                  onValueChange={(v, fields) => {
                    const chosen =
                      'package_id' in fields && fields.package_id
                        ? ing?.packages?.find((p) => p.id === fields.package_id)
                        : undefined;
                    setLine(i, {
                      unit: v,
                      package_size: chosen ? String(chosen.quantity) : pkg ? '' : l.package_size,
                    });
                  }}
                />
                <Field
                  className="po-price"
                  label="$ per package"
                  inputMode="decimal"
                  value={l.unit_price}
                  onChange={(e) => setLine(i, { unit_price: e.target.value })}
                />
                <Button
                  type="button"
                  className="po-rm"
                  variant="ghost"
                  aria-label="Remove line"
                  style={{ width: 44, padding: 0 }}
                  onClick={() =>
                    setLines(lines.length > 1 ? lines.filter((_, j) => j !== i) : [blank()])
                  }
                >
                  ×
                </Button>
              </div>
            );
          })}
        </div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginTop: 16,
          }}
        >
          <Button type="button" variant="secondary" onClick={() => setLines([...lines, blank()])}>
            + Line
          </Button>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 12,
              alignItems: 'center',
              marginLeft: 'auto',
            }}
          >
            <strong>Total {money(total)}</strong>
            <Button
              type="button"
              disabled={!valid || !locationId || post.isPending}
              onClick={() => {
                setMsg(null);
                post.mutate({
                  location_id: locationId,
                  vendor_id: vendor || null,
                  reference: reference.trim() || undefined,
                  items: lines.map((l) => {
                    const f = splitUnitValue(l.unit);
                    const common = {
                      ingredient_id: l.ingredient_id,
                      package_count: Number(l.package_count),
                      unit_price_cents: Math.round(Number(l.unit_price) * 100),
                    };
                    return 'package_id' in f && f.package_id
                      ? { ...common, package_id: f.package_id }
                      : {
                          ...common,
                          package_size: Number(l.package_size),
                          package_unit: 'unit' in f ? f.unit : undefined,
                        };
                  }),
                });
              }}
            >
              {post.isPending ? 'Posting…' : 'Post receipt'}
            </Button>
          </div>
        </div>
        {msg && (
          <div role="status" style={{ marginTop: 12 }}>
            <Notice kind={msg.kind}>{msg.text}</Notice>
          </div>
        )}
      </Card>

      <Card style={{ minWidth: 0 }}>
        <strong>Recent receipts</strong>
        {receipts.isError && (
          <Notice kind="error">Could not load receipts. {errorText(receipts.error, '')}</Notice>
        )}
        {listMsg && (
          <div role="status" style={{ margin: '8px 0' }}>
            <Notice kind={listMsg.kind}>{listMsg.text}</Notice>
          </div>
        )}
        <div className="po-list">
          <table className="po-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Vendor</th>
                <th>Status</th>
                <th>Total</th>
                <th>
                  <span className="po-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {(receipts.data ?? []).length === 0 && (
                <tr>
                  <td colSpan={5} className="po-empty">
                    {receipts.isLoading
                      ? 'Loading…'
                      : 'No receipts yet. Your first receipt is what gives every pizza a food cost.'}
                  </td>
                </tr>
              )}
              {(receipts.data ?? []).map((r) => (
                <tr key={r.id}>
                  <td>
                    <span className="po-label">When</span>
                    <span className="po-nowrap">{when(r.purchased_at, 'Never')}</span>
                    {r.entered_by && <span className="po-sub">by {r.entered_by}</span>}
                  </td>
                  <td>
                    <span className="po-label">Vendor</span>
                    {r.vendor ?? 'No vendor'}
                    {r.reference && <span className="po-sub">{r.reference}</span>}
                  </td>
                  <td>
                    <span className="po-label">Status</span>
                    <span style={{ color: r.status === 'void' ? 'var(--ink-soft)' : undefined }}>
                      {STATUS_LABEL[r.status] ?? r.status}
                    </span>
                    {canDelete && r.status === 'posted' && r.can_void === false && (
                      <span className="po-sub">Stock already used, so it can't be voided.</span>
                    )}
                  </td>
                  <td>
                    <span className="po-label">Total</span>
                    <span
                      style={{ textDecoration: r.status === 'void' ? 'line-through' : undefined }}
                    >
                      {r.total_cents === null ? 'No total' : money(r.total_cents)}
                    </span>
                    <span className="po-sub">{plural(r.line_count, 'line')}</span>
                  </td>
                  <td className="po-wide">
                    <div className="po-actions">
                      <HistoryLink
                        entityType="purchase_receipts"
                        entityId={r.id}
                        name={receiptName(r)}
                      />
                      {canDelete && canAdjust && r.status === 'posted' && r.can_void === false && (
                        <Button
                          type="button"
                          variant="secondary"
                          aria-label={`Adjust stock on the Inventory page to correct ${receiptName(r)}`}
                          onClick={() => navigate('/inventory')}
                        >
                          Adjust stock
                        </Button>
                      )}
                      {canDelete && r.status === 'posted' && r.can_void !== false && (
                        <Button
                          type="button"
                          variant="danger"
                          aria-label={`Void ${receiptName(r)}`}
                          onClick={() => {
                            setListMsg(null);
                            setVoiding(r);
                          }}
                        >
                          Void
                        </Button>
                      )}
                      {canDelete && r.status === 'draft' && (
                        <Button
                          type="button"
                          variant="danger"
                          aria-label={`Delete ${receiptName(r)}`}
                          onClick={() => {
                            setListMsg(null);
                            setDeletingReceipt(r);
                          }}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card style={{ minWidth: 0 }}>
        <strong>Vendors</strong>
        <p style={{ margin: '4px 0 8px', fontSize: 14, color: 'var(--ink-soft)' }}>
          Where you buy. A vendor on old receipts can't be deleted, but you can deactivate it so it
          stops showing up when you enter a receipt.
        </p>
        {vendors.isError && (
          <Notice kind="error">Could not load vendors. {errorText(vendors.error, '')}</Notice>
        )}
        {vendorMsg && (
          <div role="status" style={{ margin: '8px 0' }}>
            <Notice kind={vendorMsg.kind}>{vendorMsg.text}</Notice>
          </div>
        )}
        <div className="po-list">
          <table className="po-table">
            <thead>
              <tr>
                <th>Vendor</th>
                <th>Used on</th>
                <th>
                  <span className="po-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {(vendors.data ?? []).length === 0 && (
                <tr>
                  <td colSpan={3} className="po-empty">
                    {vendors.isLoading
                      ? 'Loading…'
                      : 'No vendors yet. Add one above when you enter a receipt.'}
                  </td>
                </tr>
              )}
              {(vendors.data ?? []).map((v) => (
                <tr key={v.id}>
                  <td>
                    <strong>{v.name}</strong>
                    {!v.is_active && <span className="po-inactive">Inactive</span>}
                  </td>
                  <td>
                    <span className="po-label">Used on</span>
                    {usesText(v.uses)}
                    {canDelete && !v.can_delete && (
                      <span className="po-sub">On old receipts, so it can't be deleted.</span>
                    )}
                  </td>
                  <td className="po-wide">
                    <div className="po-actions">
                      <HistoryLink entityType="vendors" entityId={v.id} name={v.name} />
                      <Button
                        type="button"
                        variant="secondary"
                        aria-label={`${v.is_active ? 'Deactivate' : 'Reactivate'} ${v.name}`}
                        disabled={setActive.isPending}
                        onClick={() => {
                          setVendorMsg(null);
                          setActive.mutate({ v, active: !v.is_active });
                        }}
                      >
                        {v.is_active ? 'Deactivate' : 'Reactivate'}
                      </Button>
                      {canDelete && v.can_delete && (
                        <Button
                          type="button"
                          variant="danger"
                          aria-label={`Delete ${v.name}`}
                          onClick={() => {
                            setVendorMsg(null);
                            setDeletingVendor(v);
                          }}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {voiding && (
        <ConfirmDelete
          open
          onClose={() => setVoiding(null)}
          title={`Void ${receiptName(voiding)}?`}
          description="Voiding takes this receipt's stock back out of inventory and stops counting its cost. The receipt stays on the record, marked Voided."
          consequencesTitle="What changes:"
          consequences={[
            `${plural(voiding.line_count, 'line')} of stock ${voiding.line_count === 1 ? 'comes' : 'come'} back out`,
            voiding.total_cents === null
              ? 'Its cost no longer counts toward food cost'
              : `${money(voiding.total_cents)} no longer counts toward food cost`,
          ]}
          askReason
          actionLabel="Void receipt"
          pendingLabel="Voiding…"
          onConfirm={async ({ reason }) => {
            try {
              await api.post(`/admin/inventory/receipts/${voiding.id}/void`, { reason });
            } catch (e) {
              // A refusal means the list was out of date; refresh it so the row explains why.
              refreshReceipts();
              throw e;
            }
            setListMsg({
              kind: 'ok',
              text: 'Receipt voided. Its stock came back out of inventory.',
            });
            refreshReceipts();
          }}
          alternative={{
            label: 'Go to Inventory',
            description:
              'To fix the numbers instead, open Inventory and use Adjust stock on each ingredient.',
            onClick: () => navigate('/inventory'),
          }}
        />
      )}
      {deletingReceipt && (
        <ConfirmDelete
          open
          onClose={() => setDeletingReceipt(null)}
          title={`Delete ${receiptName(deletingReceipt)}?`}
          description="This draft never added stock, so deleting it changes nothing else."
          consequences={[plural(deletingReceipt.line_count, 'line')]}
          askReason
          onConfirm={async ({ reason }) => {
            await api.delete(withReason(`/admin/inventory/receipts/${deletingReceipt.id}`, reason));
            setListMsg({ kind: 'ok', text: 'Draft receipt deleted.' });
            refreshReceipts();
          }}
          alternative={{
            label: 'Void instead',
            description: 'A posted receipt is voided, which takes its stock back out.',
            onClick: () => {
              const r = deletingReceipt;
              setDeletingReceipt(null);
              setVoiding({ ...r, status: 'posted' });
            },
          }}
        />
      )}
      {deletingVendor && (
        <ConfirmDelete
          open
          onClose={() => setDeletingVendor(null)}
          title={`Delete ${deletingVendor.name}?`}
          description="Deleting removes this vendor for good."
          consequencesTitle="Also:"
          consequences={
            deletingVendor.uses.planned_purchases
              ? [
                  `${plural(deletingVendor.uses.planned_purchases, 'planned purchase')} will no longer name a vendor`,
                ]
              : undefined
          }
          askReason
          onConfirm={async ({ reason }) => {
            try {
              await api.delete(withReason(`/admin/inventory/vendors/${deletingVendor.id}`, reason));
            } catch (e) {
              // A refusal means the list was out of date; refresh it so the row explains why.
              void qc.invalidateQueries({ queryKey: ['vendors'] });
              throw e;
            }
            setVendorMsg({ kind: 'ok', text: `${deletingVendor.name} is deleted.` });
            if (vendor === deletingVendor.id) setVendor('');
            void qc.invalidateQueries({ queryKey: ['vendors'] });
          }}
          alternative={
            deletingVendor.is_active
              ? {
                  label: 'Deactivate',
                  pendingLabel: 'Deactivating…',
                  description:
                    'Deactivating keeps it on old receipts and hides it when you enter a new one.',
                  onClick: async () => {
                    const v = deletingVendor;
                    await api.patch(`/admin/inventory/vendors/${v.id}`, { is_active: false });
                    setVendorMsg({
                      kind: 'ok',
                      text: `${v.name} is deactivated. Old receipts keep it, and it no longer shows up for new ones.`,
                    });
                    if (vendor === v.id) setVendor('');
                    void qc.invalidateQueries({ queryKey: ['vendors'] });
                  },
                }
              : undefined
          }
        />
      )}
    </Section>
  );
}
